import currency from 'currency.js';

import { getCurrency } from './localeCurrency';

import currencyData from '@/data/currencies.json';

// Stripe takes these zero-decimal currencies in hundredths, so their stored amounts carry two
// extra digits that currency.js does not know about.
const TWO_DECIMAL_STORED_CURRENCIES = new Set(['ISK']);

function storedAmountToMajorUnits(amountMinorUnits: number, currencyCode: string, precision: number): number {
  if (TWO_DECIMAL_STORED_CURRENCIES.has(currencyCode.toUpperCase())) {
    return amountMinorUnits / 100;
  }
  return currency(amountMinorUnits, { fromCents: true, precision }).value;
}

// Universal Currency Formatter with Locale Support Patch
function formatCurrency(amount: number, locale: string, currency: string): string {
  const currencySymbols: Record<string, string> = {
    AUD: 'A$',
    CAD: 'CA$',
    USD: '$',
  };

  try {
    const formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
    });

    let formatted = formatter.format(amount);

    // Force the correct currency symbol based on currency code
    if (currency in currencySymbols) {
      // Get the correct currency symbol
      const correctSymbol = currencySymbols[currency];

      // Replace the currency symbol part while preserving the locale-specific number formatting
      // This regular expression matches any non-digit, non-comma, non-dot, non-space at the beginning
      formatted = formatted.replace(/^[^\d\s.,]+/, correctSymbol);
    }

    return formatted;
  } catch (error) {
    console.error('Intl.NumberFormat Error:', error);
    // Fallback if Intl.NumberFormat completely fails
    return `${currency in currencySymbols ? currencySymbols[currency] : ''}${amount.toFixed(2)}`;
  }
}

/**
 * Formats the amount in minor units (cents, pence, etc.) to a string based on the provided currency code and locale.
 * @param {number} amountMinorUnits - The amount in minor units (e.g., 12345 for $123.45).
 * @param {string} currencyCode - The ISO 4217 currency code (e.g., "USD", "JPY").
 * @param {string} locale - The locale to format the output (default is "en-US").
 * @returns {string} - The formatted money string (e.g., "$123.45").
 */
export function formatMoney(
  amountMinorUnits: number,
  currencyCode: string = getCurrency('en-US'),
  locale?: string,
): string {
  const safeLocale = locale || 'en-US';
  const currencyInfo = currencyData[currencyCode.toUpperCase() as keyof typeof currencyData];

  if (!currencyInfo) {
    throw new Error(`Currency ${currencyCode} not found in currency data.`);
  }

  const value = storedAmountToMajorUnits(amountMinorUnits, currencyCode, currencyInfo.ISOdigits);

  return formatCurrency(value, safeLocale, currencyCode);
}

/**
 * Utility function to get currency information based on the ISO 4217 currency code.
 * Uses the preloaded currency data from the JSON file.
 */
export function getCurrencyInfo(currencyCode: string) {
  return currencyData[currencyCode as keyof typeof currencyData] || null;
}

export function moneyStringOrNumberToMinorUnits(
  moneyStringOrNumber: string | number,
  currencyCode: string,
): number {
  const currencyInfo = currencyData[currencyCode.toUpperCase() as keyof typeof currencyData];

  if (!currencyInfo) {
    throw new Error(`Currency ${currencyCode} not found in currency data.`);
  }

  // Convert user input to currency.js format
  const currencyValue = currency(moneyStringOrNumber, { precision: currencyInfo.ISOdigits });

  // Note: The backend stores all amounts with 2 decimal places (multiplied by 100),
  // regardless of the currency's ISOdigits. For currencies with ISOdigits: 0 or 3,
  // we need to multiply by 100 to match the backend's storage format.
  if (currencyInfo.ISOdigits === 0 || currencyInfo.ISOdigits === 3) {
    // For currencies with 0 or 3 decimal places, multiply by 100 to match backend storage
    // Example: User types "100" JPY → backend expects 10000 (100 * 100)
    return currencyValue.intValue * 100;
  }

  // For currencies with 2 decimal places (most common), intValue is already correct
  // Example: User types "100" USD → currency.js treats as $100.00 → intValue = 10000 ✓
  return currencyValue.intValue;
}

export function minorUnitsToCurrencyJs(amountMinorUnits: number, currencyCode: string) {
  const currencyInfo = currencyData[currencyCode.toUpperCase() as keyof typeof currencyData];

  if (!currencyInfo) {
    throw new Error(`Currency ${currencyCode} not found in currency data.`);
  }

  // Note: The backend stores all amounts with 2 decimal places (multiplied by 100),
  // regardless of the currency's ISOdigits. For currencies with ISOdigits: 0 or 3,
  // currency.js with precision might not handle this correctly, so we manually divide by 100.
  let majorUnits: number;
  if (currencyInfo.ISOdigits === 0 || currencyInfo.ISOdigits === 3) {
    // Backend stores amounts with 2 decimal places for all currencies
    // For currencies with 0 or 3 decimal places, divide by 100 to convert from backend format
    majorUnits = amountMinorUnits / 100;
  } else {
    majorUnits = currency(amountMinorUnits, {
      fromCents: true,
      precision: currencyInfo.ISOdigits,
      symbol: currencyInfo.symbol,
    }).value;
  }

  // Create currency.js object with the correct precision for display
  return currency(majorUnits, {
    fromCents: false,
    precision: currencyInfo.ISOdigits,
    symbol: currencyInfo.symbol,
  });
}

export function minorUnitsToMajorUnits(amountMinorUnits: number, currencyCode: string): number {
  const currencyInfo = currencyData[currencyCode.toUpperCase() as keyof typeof currencyData];

  if (!currencyInfo) {
    throw new Error(`Currency ${currencyCode} not found in currency data.`);
  }

  return storedAmountToMajorUnits(amountMinorUnits, currencyCode, currencyInfo.ISOdigits);
}

export function convertCurrencyJsObject(currencyJsInput: any, rate: number, outputCurrencyCode: string) {
  const convertedValue = currencyJsInput.multiply(rate);

  const outputCurrencyInfo = currencyData[outputCurrencyCode as keyof typeof currencyData];

  return currency(convertedValue.value as number, {
    fromCents: false,
    precision: outputCurrencyInfo.ISOdigits,
    symbol: outputCurrencyInfo.symbol,
  });
}

export function convertMinorUnits(
  amountMinorUnits: number,
  inputCurrencyCode: string,
  rate: number,
  outputCurrencyCode: string,
) {
  return convertCurrencyJsObject(
    minorUnitsToCurrencyJs(amountMinorUnits, inputCurrencyCode),
    rate,
    outputCurrencyCode,
  ).intValue;
}

export const formatMoneyWithoutSymbol = ({
  amount,
  fromCents = true,
}: {
  amount: number;
  currencyCode: string;
  fromCents?: boolean;
}): string => {
  const value = fromCents ? amount / 100 : amount;
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

export const convertCurrency = (
  amount: number,
  fromCurrency: string,
  toCurrency: string,
  rate: number,
): number => {
  if (fromCurrency === toCurrency) return amount;
  return Math.round(amount * rate);
};